import { Stamp, IconOrders } from '~/design-system';
import { ORDER_TONE } from '~/platform/domain/presentation';
import { getApi } from '~/platform/data/apiSingleton';
import type { ModuleDefinition } from '~/platform/registry/types';

/* ------------------------------------------------------------------ *
 * A feature module is one file like this plus a folder of screens.
 * The shell reads it; nothing in the shell knows what an order is.
 * ------------------------------------------------------------------ */

export const ordersModule: ModuleDefinition = {
  id: 'orders',
  i18nNamespace: 'orders',
  requires: { subject: 'order', action: 'read' },

  nav: {
    labelKey: 'orders:nav',
    icon: IconOrders,
    to: 'orders',
    group: 'trade',
    order: 10,
  },

  routes: [
    { path: 'orders', component: () => import('./OrdersPage') },
    {
      path: 'orders/new',
      component: () => import('./OrderEditorPage'),
      requires: { subject: 'order', action: 'create' },
    },
    { path: 'orders/$orderId', component: () => import('./OrderDetailPage') },
    {
      path: 'orders/$orderId/edit',
      component: () => import('./OrderEditorPage'),
      requires: { subject: 'order', action: 'update' },
    },
  ],

  commands: [
    {
      id: 'orders.new',
      labelKey: 'orders:newOrder',
      groupKey: 'orders:title',
      shortcut: 'N',
      requires: { subject: 'order', action: 'create' },
      run: ({ businessSlug, navigate }) => navigate(`/b/${businessSlug}/orders/new`),
    },
    {
      id: 'orders.today',
      labelKey: 'orders:title',
      groupKey: 'orders:title',
      run: ({ businessSlug, navigate }) => navigate(`/b/${businessSlug}/orders`),
    },
  ],

  /* Contributes rows to the global palette. Runs in parallel with
     every other module's provider and is allowed to fail quietly. */
  search: async (query, { businessId, businessSlug }) => {
    const orders = await getApi().listOrders(businessId, { search: query, limit: 8 });
    return orders.slice(0, 8).map((order) => ({
      id: order.id,
      moduleId: 'orders' as const,
      code: order.number,
      title: order.customerName,
      subtitle: `${order.lines.length} · ${order.deliveryDate}`,
      handlingClass: 'neutral',
      stamp: <Stamp tone={ORDER_TONE[order.status]} size="sm">{order.status}</Stamp>,
      to: `/b/${businessSlug}/orders/${order.id}`,
    }));
  },
};
